"use client";

import { ArrowLeft, Bug, Check, ChevronRight, ExternalLink, Eye, EyeOff, Wrench, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "scout:debug-clerk-boundaries";

type DevToolsView = "main" | "previews";
type PreviewLinkCategory = "Core Auth" | "Enterprise First Admin" | "Billing And Desktop Completion";

type PreviewLink = {
  category: PreviewLinkCategory;
  screen: string;
  note: string;
  href: string;
};

const PREVIEW_LINK_CATEGORIES = [
  "Core Auth",
  "Enterprise First Admin",
  "Billing And Desktop Completion",
] as const satisfies readonly PreviewLinkCategory[];

export const SCOUT_DEV_TOOLS_PREVIEW_LINKS = [
  {
    category: "Core Auth",
    screen: "Sign in",
    note: "Live Clerk drop-in.",
    href: "/auth/sign-in",
  },
  {
    category: "Core Auth",
    screen: "Sign up",
    note: "Live Clerk drop-in.",
    href: "/auth/sign-up",
  },
  {
    category: "Enterprise First Admin",
    screen: "Enterprise link loading",
    note: "Visual handoff state.",
    href: "/auth/enterprise-link?preview=loading",
  },
  {
    category: "Enterprise First Admin",
    screen: "Enterprise link error",
    note: "Expired or used link state.",
    href: "/auth/enterprise-link?preview=error",
  },
  {
    category: "Enterprise First Admin",
    screen: "Enterprise password setup",
    note: "Preview-only password form.",
    href: "/auth/enterprise-password?preview=1",
  },
  {
    category: "Billing And Desktop Completion",
    screen: "Billing completion waiting",
    note: "Stripe confirmation wait.",
    href: "/billing/complete?preview=waiting",
  },
  {
    category: "Billing And Desktop Completion",
    screen: "Billing completion delayed",
    note: "Retry action visible.",
    href: "/billing/complete?preview=delayed",
  },
  {
    category: "Billing And Desktop Completion",
    screen: "Billing completion finishing",
    note: "Checkout finishing state.",
    href: "/billing/complete?preview=finishing",
  },
  {
    category: "Billing And Desktop Completion",
    screen: "Desktop link loading",
    note: "Desktop handoff state.",
    href: "/desktop/complete?preview=loading",
  },
  {
    category: "Billing And Desktop Completion",
    screen: "Desktop link error",
    note: "Expired desktop link state.",
    href: "/desktop/complete?preview=error",
  },
] as const satisfies readonly PreviewLink[];

function applyClerkBoundaryDebug(enabled: boolean) {
  document.documentElement.toggleAttribute("data-debug-clerk-boundaries", enabled);
}

function stripDebugQueryParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("debug_clerk")) return;

  url.searchParams.delete("debug_clerk");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
}

export function DevClerkBoundaryDebug() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<DevToolsView>("main");
  const [clerkBoundaries, setClerkBoundaries] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const params = new URLSearchParams(window.location.search);
    const requested = params.get("debug_clerk");

    if (requested === "1" || requested === "true") {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } else if (requested === "0" || requested === "false") {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    stripDebugQueryParam();

    const enabled = window.localStorage.getItem(STORAGE_KEY) === "1";
    setClerkBoundaries(enabled);
    applyClerkBoundaryDebug(enabled);
    setReady(true);

    return () => {
      document.documentElement.removeAttribute("data-debug-clerk-boundaries");
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (process.env.NODE_ENV === "production" || !ready) return null;

  function toggleClerkBoundaries() {
    const next = !clerkBoundaries;
    setClerkBoundaries(next);
    if (next) {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    applyClerkBoundaryDebug(next);
  }

  function closeDevTools() {
    setOpen(false);
    setView("main");
  }

  function navigateToPreview(href: string) {
    window.location.assign(href);
  }

  return (
    <div ref={rootRef} className="fixed bottom-4 right-4 z-[2147483000] text-foreground">
      {open ? (
        <div
          role="menu"
          aria-label="Scout developer tools"
          className="absolute bottom-14 right-0 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border/90 bg-background/95 shadow-2xl shadow-black/50 backdrop-blur"
        >
          {view === "main" ? (
            <div className="flex items-center justify-between border-b border-border/80 px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Scout Dev Tools</h2>
                <p className="text-xs text-muted-foreground">Local development controls</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:text-foreground"
                title="Close developer tools"
                onClick={closeDevTools}
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 border-b border-border/80 px-3 py-2.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:text-foreground"
                title="Back to developer tools"
                onClick={() => setView("main")}
              >
                <ArrowLeft className="size-4" />
              </Button>
              <h2 className="flex-1 text-sm font-semibold text-foreground">Preview links</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:text-foreground"
                title="Close developer tools"
                onClick={closeDevTools}
              >
                <X className="size-4" />
              </Button>
            </div>
          )}

          {view === "main" ? (
            <div className="divide-y divide-border/80">
              <div className="flex items-center justify-between gap-4 px-4 py-4">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border border-border/80 bg-muted/40 text-muted-foreground">
                    <Bug className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">Clerk boundaries</div>
                    <div className="mt-0.5 text-xs leading-5 text-muted-foreground">Outline Clerk-owned auth UI.</div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={clerkBoundaries ? "default" : "outline"}
                  size="sm"
                  className="h-8 shrink-0 px-2.5 text-xs"
                  aria-pressed={clerkBoundaries}
                  title={clerkBoundaries ? "Hide Clerk boundaries" : "Show Clerk boundaries"}
                  onClick={toggleClerkBoundaries}
                >
                  {clerkBoundaries ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                  {clerkBoundaries ? "On" : "Off"}
                </Button>
              </div>

              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setView("previews")}
              >
                <span>
                  <span className="block text-sm font-medium text-foreground">Preview links</span>
                  <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">Browse local auth and billing states.</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>

              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="text-xs font-medium text-muted-foreground">Environment</div>
                <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border/80 bg-muted/35 px-2 text-xs font-medium text-muted-foreground">
                  <Check className="size-3.5 text-primary" />
                  Local only
                </span>
              </div>
            </div>
          ) : (
            <div className="max-h-[min(32rem,calc(100vh-8rem))] overflow-y-auto px-2 py-2">
              {PREVIEW_LINK_CATEGORIES.map((category) => (
                <section key={category} className="py-1.5">
                  <h3 className="px-2 pb-1.5 text-xs font-semibold text-muted-foreground">
                    {category}
                  </h3>
                  <div className="grid gap-1">
                    {SCOUT_DEV_TOOLS_PREVIEW_LINKS.filter((link) => link.category === category).map((link) => (
                      <button
                        key={link.href}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => navigateToPreview(link.href)}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground" title={link.screen}>
                            {link.screen}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground" title={link.note}>
                            {link.note}
                          </span>
                        </span>
                        <ExternalLink className="size-4 shrink-0 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11 rounded-full border border-border/90 bg-background/95 text-foreground shadow-xl shadow-black/50 hover:bg-muted/80"
          aria-expanded={open}
          title="Developer tools"
          onClick={() => {
            setOpen((value) => !value);
            setView("main");
          }}
        >
          <Wrench className="size-4" />
        </Button>
      </div>
    </div>
  );
}
